import { Card, Tag, Typography, Flex } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import Image from './Image';
import { formatLastScanDate } from './utils/dateUtils';
import { getRiskColors } from './utils/cardUtils';

const { Text } = Typography;

export interface RepositoryCardProps {
  imageUrl: string;
  repositoryName: string;
  latestScanUrl?: string;
  latestScanDate?: string;
  ownerType: string;
  description: string;
  stars: number;
  primaryLanguage: string;
  toolsCount: number;
  risk: string;
  vulnerableRuleIds?: string;
  vulnerableRuleNames?: string;
  airtableId: string;
  /** Card click handler — caller decides where to navigate. */
  onClick?: () => void;
}

const RepositoryCard = ({
  imageUrl,
  repositoryName,
  latestScanDate,
  ownerType,
  description,
  stars,
  primaryLanguage,
  toolsCount,
  risk,
  onClick,
}: RepositoryCardProps) => {
  const formattedLastScan = formatLastScanDate(latestScanDate);
  const displayDescription = description.length > 155 ? `${description.substring(0, 155)}...` : description;

  const riskColors = getRiskColors(risk);

  const cardBgClass = 'bg-white dark:bg-[#141414]';
  const cardBorderClass = 'border-[rgba(0,0,0,0.06)] dark:border-[#303030]';
  const headingTextClass = 'text-[rgba(0,0,0,0.88)] dark:text-[rgba(255,255,255,0.85)]';
  const descriptionTextClass = 'text-[rgba(0,0,0,0.45)] dark:text-[rgba(255,255,255,0.45)]';
  const separatorClass = 'bg-[#F0F0F0] dark:bg-[#303030]';

  const ownerTypeBg = 'var(--owner-valid-org-bg)';
  const ownerTypeBorder = 'var(--owner-valid-org-border)';
  const ownerTypeText = 'var(--owner-valid-org-text)';

  return (
    <Card
      className={`w-full rounded-lg border cursor-pointer ${cardBorderClass} ${cardBgClass}`}
      classNames={{
        body: 'p-3 flex flex-col',
      }}
      onClick={onClick}
    >
      <Flex vertical gap={8} className="w-full">
        <Flex gap={16} align="flex-start" className="h-[84px]">
          <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
            <Image
              src={imageUrl}
              alt={repositoryName}
              className="w-full h-full object-cover"
            />
          </div>
          <Flex vertical className="flex-1 min-w-0 h-full">
            <Text
              strong
              className={`text-base leading-6 h-6 block ${headingTextClass}`}
              ellipsis={{ tooltip: repositoryName }}
            >
              {repositoryName}
            </Text>
            <Text
              className={`text-xs leading-5 flex-1 line-clamp-3 ${descriptionTextClass}`}
            >
              {displayDescription}
            </Text>
          </Flex>
        </Flex>

        <Flex vertical className="w-full">
          <div className={`w-full h-px ${separatorClass}`} />

          <Flex gap={8} className="pt-2 pb-3">
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Risk
              </Text>
              <Tag
                style={{
                  backgroundColor: riskColors.bg,
                  borderColor: riskColors.border,
                  color: riskColors.text,
                }}
                className="m-0 h-[22px] inline-flex items-center w-fit"
              >
                {risk || 'Unknown'}
              </Tag>
            </Flex>
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Owner Type
              </Text>
              <Tag
                style={{
                  backgroundColor: ownerTypeBg,
                  borderColor: ownerTypeBorder,
                  color: ownerTypeText,
                }}
                className="m-0 h-[22px] inline-flex items-center gap-1 w-fit"
                icon={<CheckCircleOutlined className="text-xs" />}
              >
                {ownerType === 'valid-org' || ownerType === 'Valid Org' ? 'Valid Org' : ownerType}
              </Tag>
            </Flex>
          </Flex>

          <div className={`w-full h-px ${separatorClass}`} />

          <Flex gap={8} className="pt-2 pb-3">
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Language
              </Text>
              <Text className={`text-xs leading-5 ${descriptionTextClass}`}>
                {primaryLanguage}
              </Text>
            </Flex>
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Stars
              </Text>
              <Text className={`text-xs leading-5 ${descriptionTextClass}`}>
                {stars.toLocaleString()}
              </Text>
            </Flex>
          </Flex>

          <div className={`w-full h-px ${separatorClass}`} />

          <Flex gap={8} className="pt-2 pb-3">
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Tools
              </Text>
              <Text className={`text-xs leading-5 ${descriptionTextClass}`}>
                {toolsCount}
              </Text>
            </Flex>
            <Flex vertical className="flex-1">
              <Text strong className={`text-xs leading-5 ${headingTextClass}`}>
                Last Scan
              </Text>
              <Text className={`text-xs leading-5 whitespace-nowrap ${descriptionTextClass}`}>
                {formattedLastScan || 'N/A'}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export default RepositoryCard;
